<template>
	<div class="container-fluid p-4">
		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<!-- Setting a trap: the facts the operator states when a trap goes out.
		     Only the Site and the set datetime are required — an off-station trap
		     with no type, lure or coordinates is a normal instance. -->
		<div class="card">
			<div class="card-body">
				<h2 class="h5 mb-0">New Trap Instance</h2>
				<p class="text-muted mb-3 small">
					A trap instance is one trap put out at one place and time. Only the
					Site and the set datetime are required.
				</p>

				<form
					class="row g-2 align-items-end mb-3"
					@submit.prevent="createInstance"
				>
					<div class="col-12 col-md-4 col-lg-3">
						<label class="form-label" for="instanceSite">Site</label>
						<select
							id="instanceSite"
							v-model="newInstance.site_id"
							class="form-select"
							required
						>
							<option :value="null" disabled>Choose a site</option>
							<option v-for="site in sites" :key="site.id" :value="site.id">
								{{ siteLabel(site) }}
							</option>
						</select>
					</div>
					<div class="col-12 col-md-4 col-lg-3">
						<label class="form-label" for="instanceSetAt">Set</label>
						<input
							id="instanceSetAt"
							v-model="newInstance.set_at"
							type="datetime-local"
							class="form-control"
							required
						/>
					</div>
					<div class="col-12 col-md-4 col-lg-3">
						<label class="form-label" for="instanceStation">Station</label>
						<select
							id="instanceStation"
							v-model="newInstance.station_id"
							class="form-select"
						>
							<option :value="null">No station (off-station)</option>
							<option
								v-for="station in stations"
								:key="station.id"
								:value="station.id"
							>
								{{ stationLabel(station) }}
							</option>
						</select>
					</div>
					<div class="col-12 col-md-4 col-lg-3">
						<label class="form-label" for="instanceTrapType">Trap type</label>
						<select
							id="instanceTrapType"
							v-model="newInstance.trap_type_id"
							class="form-select"
						>
							<option :value="null">Not stated</option>
							<option
								v-for="entry in vocabulary.trap_type"
								:key="entry.id"
								:value="entry.id"
							>
								{{ vocabularyLabel(entry) }}
							</option>
						</select>
					</div>
					<div class="col-12 col-md-4 col-lg-3">
						<label class="form-label" for="instanceLure">Lures</label>
						<select
							id="instanceLure"
							v-model="newInstance.lures"
							class="form-select"
							multiple
							size="4"
						>
							<option
								v-for="entry in vocabulary.lure"
								:key="entry.id"
								:value="entry.id"
							>
								{{ vocabularyLabel(entry) }}
							</option>
						</select>
						<div class="form-text">
							Hold Ctrl/⌘ to select more than one; none for not stated.
						</div>
					</div>
					<div class="col-6 col-md-3 col-lg-2">
						<label class="form-label" for="instanceLatitude">Latitude</label>
						<input
							id="instanceLatitude"
							v-model="newInstance.latitude"
							type="number"
							step="any"
							class="form-control"
							placeholder="optional"
						/>
					</div>
					<div class="col-6 col-md-3 col-lg-2">
						<label class="form-label" for="instanceLongitude">Longitude</label>
						<input
							id="instanceLongitude"
							v-model="newInstance.longitude"
							type="number"
							step="any"
							class="form-control"
							placeholder="optional"
						/>
					</div>
					<div class="col-12 col-md-4 col-lg-3">
						<label class="form-label" for="instanceCollectedAt">Collect</label>
						<input
							id="instanceCollectedAt"
							v-model="newInstance.collected_at"
							type="datetime-local"
							class="form-control"
						/>
					</div>
					<div class="col-12 col-md-auto">
						<ButtonLoading
							type="submit"
							text="Add instance"
							icon="bi bi-plus-lg"
							variant="primary"
							:loading="savingInstance"
							:disabled="!instanceValid"
						/>
					</div>
				</form>
			</div>
		</div>

		<!-- The district's instances, newest set first. Location is `—` for an
		     instance whose set form stated no coordinates. -->
		<div class="card mt-4">
			<div class="card-body">
				<h2 class="h5 mb-0">Trap Instances</h2>
				<p class="text-muted mb-3 small">
					The district's trap instances, newest set first.
				</p>

				<div v-if="loading" class="text-center py-4">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>

				<div
					v-else-if="instances.length === 0"
					class="text-center py-4 text-muted"
				>
					No trap instances yet.
				</div>

				<div v-else class="table-responsive">
					<table class="table table-striped align-middle">
						<thead class="table-light">
							<tr>
								<th scope="col">Set</th>
								<th scope="col">Collected</th>
								<th scope="col">Station</th>
								<th scope="col">Site</th>
								<th scope="col">Trap type</th>
								<th scope="col">Lure</th>
								<th scope="col">Location</th>
							</tr>
						</thead>
						<tbody>
							<!-- The set datetime is the row's own identity cell, so
							     it is the way into the instance's page
							     (/_/traps/:id) — the surface the trap's printed
							     code is checked against (§7.2, Q-T1). -->
							<tr v-for="instance in instances" :key="instance.id">
								<td>
									<RouterLink :to="`/_/traps/${instance.id}`">
										{{ formatInstant(instance.date) }}
									</RouterLink>
								</td>
								<td>
									{{
										instance.collected_at
											? formatInstant(instance.collected_at)
											: "—"
									}}
									<span
										class="badge rounded-pill ms-2"
										:class="
											instance.state === 'collected'
												? 'bg-success'
												: 'bg-secondary'
										"
									>
										{{ instance.state === "collected" ? "Collected" : "Set" }}
									</span>
								</td>
								<td>{{ instance.station || "—" }}</td>
								<td>{{ instance.site }}</td>
								<td>{{ instance.trap_type || "—" }}</td>
								<td>{{ instance.lure || "—" }}</td>
								<td>{{ formatLocation(instance) }}</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- The pager: the page in hand is bounded (PAGE_SIZE), so a district
				     with thousands of instances opens this view immediately. The
				     counts are the envelope's own, and each button steps one page of
				     the resource's own default order (newest set first). -->
				<div
					v-if="instances.length > 0"
					class="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3"
				>
					<p class="text-muted small mb-0">
						Showing {{ pageStart }}–{{ pageEnd }} of {{ instanceTotal }}
						instances
					</p>
					<div class="btn-group">
						<button
							type="button"
							class="btn btn-outline-secondary btn-sm"
							:disabled="!hasPrev || paging"
							@click="goToPage(instanceOffset - PAGE_SIZE)"
						>
							<i class="bi bi-chevron-left"></i> Previous
						</button>
						<button
							type="button"
							class="btn btn-outline-secondary btn-sm"
							:disabled="!hasNext || paging"
							@click="goToPage(instanceOffset + PAGE_SIZE)"
						>
							Next <i class="bi bi-chevron-right"></i>
						</button>
					</div>
				</div>
			</div>
		</div>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { RouterLink } from "vue-router";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { formatAddress, formatDate } from "@/format";
import type {
	Address,
	MapReviewQueryEnvelope,
	MapReviewTrapInstanceRow,
	MapReviewTrapStationRow,
	MapReviewTrapVocabulary,
	MapReviewVocabularyEntry,
	Site,
	TrapInstanceCreate,
} from "@/type/api";

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

// The form's own lists: the district's Sites (the instance's required
// relation), its standing Trap Stations, and the live trap vocabularies.
const sites = ref<Site[]>([]);
const stations = ref<MapReviewTrapStationRow[]>([]);
const vocabulary = ref<MapReviewTrapVocabulary>({
	species: [],
	sex: [],
	trap_type: [],
	lure: [],
	test_method: [],
	test_target: [],
	test_status: [],
	products: [],
});
// The instances are read one page at a time through the resource's QUERY route
// (not its GET, which answers only the resource's first page, and not
// JSONQueryAll, which walked every page and made this view slow to open against
// a district with years of trapping behind it). The table is bounded at
// PAGE_SIZE rows and the pager counts against the envelope's own
// `filtered_total`, so the "of N" is the server's number rather than a guess.
const PAGE_SIZE = 50;
const instances = ref<MapReviewTrapInstanceRow[]>([]);
const instanceTotal = ref(0);
// Where the page in hand starts, as the envelope reported it — never the offset
// the request asked for, so the pager and the server cannot disagree.
const instanceOffset = ref(0);

// `loading` is the first load, when there is nothing to show yet; `paging` is a
// page move, which keeps the table in place and only holds the pager's buttons.
const loading = ref(false);
const paging = ref(false);
const error = ref("");

const pageStart = computed(() =>
	instances.value.length === 0 ? 0 : instanceOffset.value + 1,
);
const pageEnd = computed(() => instanceOffset.value + instances.value.length);
const hasPrev = computed(() => instanceOffset.value > 0);
const hasNext = computed(() => pageEnd.value < instanceTotal.value);

// A Site's name reads as the rest of the app reads it (MapReview.vue's
// popupSiteTitle): its label when that reads as a name, else the address, else
// its id. Most Sites carry no label at all, and a parcel Site is labelled with
// its APN — an identifier, not a name — so the label alone cannot name an
// option.
function siteLabel(site: Site): string {
	if (site.label && /[A-Za-z]/.test(site.label)) return site.label;
	if (hasAddress(site.address)) return formatAddress(site.address);
	return `Site #${site.id}`;
}

/** True when an address has a street number or name to show. */
function hasAddress(address?: Address): boolean {
	return !!address && (!!address.number || !!address.street);
}

// A Trap Station carries no name of its own, so the option names it by the
// site it belongs to and falls back to its id when the row carries no site
// name; a retired station is offered but labelled.
function stationLabel(station: MapReviewTrapStationRow): string {
	const label = station.site
		? `#${station.id} — ${station.site}`
		: `Station #${station.id}`;
	return station.active ? label : `${label} (retired)`;
}

// A vocabulary row with no name still needs an option the operator can read.
function vocabularyLabel(entry: MapReviewVocabularyEntry): string {
	return entry.name || `#${entry.id}`;
}

function formatInstant(value: string): string {
	return formatDate(new Date(value));
}

function formatLocation(row: MapReviewTrapInstanceRow): string {
	if (row.latitude === null || row.longitude === null) return "—";
	return `${row.latitude}, ${row.longitude}`;
}

// One page of instances at the resource's own default order (newest set
// first). The offset the envelope answers with is stored rather than the one
// asked for: the server clamps it, and the pager must follow the server.
async function loadInstances(atOffset: number) {
	const envelope = await apiClient.JSONQuery<
		MapReviewQueryEnvelope<MapReviewTrapInstanceRow>
	>("/api/trap-instance", { limit: PAGE_SIZE, offset: atOffset });
	instances.value = envelope.items;
	instanceTotal.value = envelope.metadata.filtered_total;
	instanceOffset.value = envelope.metadata.offset;
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		const [siteList, stationPage, vocab] = await Promise.all([
			apiClient.JSONGet<Site[]>("/api/site"),
			apiClient.JSONGet<MapReviewQueryEnvelope<MapReviewTrapStationRow>>(
				"/api/trap-station",
			),
			apiClient.JSONGet<MapReviewTrapVocabulary>("/api/trap-vocabulary"),
		]);
		sites.value = siteList ?? [];
		stations.value = stationPage.items;
		vocabulary.value = vocab;
		await loadInstances(0);
	} catch (e: any) {
		error.value =
			e?.response?.data?.message || e?.message || "Failed to load traps.";
		showNotification("Failed to load traps", error.value);
	} finally {
		loading.value = false;
	}
}

// A page move is its own read: it re-reads neither the form's lists nor the
// table's current page, and it does not clear the table while it is in flight,
// so stepping through the history never flashes the page empty.
async function goToPage(atOffset: number) {
	if (atOffset < 0 || (atOffset > 0 && atOffset >= instanceTotal.value)) return;
	paging.value = true;
	error.value = "";
	try {
		await loadInstances(atOffset);
	} catch (e: any) {
		error.value =
			e?.response?.data?.message || e?.message || "Failed to load traps.";
		showNotification("Failed to load traps", error.value);
	} finally {
		paging.value = false;
	}
}

// The created row's own set datetime decides where it sorts, and an operator
// may state a backdated one, so the list is re-read from the server (which
// orders newest set first) rather than spliced into place. The first page is
// re-read, not the page in hand: an instance just set is the newest, so it is
// the first page it belongs on — and a backdated one is announced by the toast
// rather than silently stranded off-page.
async function refreshInstances() {
	await loadInstances(0);
}

// ---- Setting a trap -------------------------------------------------------

// The datetimes are held as the datetime-local input's own string. A coordinate
// is held as whatever its input yields: Vue casts a number input to a number,
// and an empty one still reads "", so "both or neither" is a question about
// emptiness rather than about the type — asking for a string-only model here is
// what made .trim() throw on the first digit an operator typed.
type CoordinateInput = string | number;

const newInstance = reactive({
	site_id: null as number | null,
	station_id: null as number | null,
	trap_type_id: null as number | null,
	lures: [] as number[],
	latitude: "" as CoordinateInput,
	longitude: "" as CoordinateInput,
	set_at: "",
	collected_at: "",
});
const savingInstance = ref(false);

// The text a coordinate input yielded, however it was typed to yield it.
function coordinateText(value: CoordinateInput): string {
	return typeof value === "number" ? String(value) : value.trim();
}

const instanceValid = computed(
	() =>
		newInstance.site_id !== null &&
		newInstance.set_at !== "" &&
		(coordinateText(newInstance.latitude) === "") ===
			(coordinateText(newInstance.longitude) === ""),
);

// A blank coordinate is no coordinate; a stated one is the number it reads.
function parseCoordinate(value: CoordinateInput): number | null {
	const trimmed = coordinateText(value);
	return trimmed === "" ? null : Number(trimmed);
}

function resetInstance() {
	newInstance.site_id = null;
	newInstance.station_id = null;
	newInstance.trap_type_id = null;
	newInstance.lures = [];
	newInstance.latitude = "";
	newInstance.longitude = "";
	newInstance.set_at = "";
	newInstance.collected_at = "";
}

async function createInstance() {
	if (!instanceValid.value) return;
	savingInstance.value = true;
	const body: TrapInstanceCreate = {
		site_id: newInstance.site_id,
		station_id: newInstance.station_id,
		trap_type_id: newInstance.trap_type_id,
		lures: newInstance.lures,
		latitude: parseCoordinate(newInstance.latitude),
		longitude: parseCoordinate(newInstance.longitude),
		set_at: new Date(newInstance.set_at).toISOString(),
		collected_at: newInstance.collected_at
			? new Date(newInstance.collected_at).toISOString()
			: null,
	};
	try {
		const created = await apiClient.JSONPost<MapReviewTrapInstanceRow>(
			"/api/trap-instance",
			body,
		);
		await refreshInstances();
		resetInstance();
		showNotification(
			"Trap instance added",
			`Set ${formatInstant(created.date)} at ${created.site}.`,
		);
	} catch (e: any) {
		showNotification(
			"Failed to add trap instance",
			e?.response?.data?.message || e?.message || "Please try again.",
		);
	} finally {
		savingInstance.value = false;
	}
}

onMounted(load);
</script>

<style scoped>
.table-responsive {
	overflow-x: auto;
}

th {
	white-space: nowrap;
}
</style>
